import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const AutoMatchContext = createContext();

export const useAutoMatch = () => {
  const context = useContext(AutoMatchContext);
  if (!context) {
    throw new Error('useAutoMatch must be used within AutoMatchProvider');
  }
  return context;
};

export const AutoMatchProvider = ({ children }) => {
  const [progress, setProgress] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  const pollProgress = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/copyright/auto-match/status`, {
        withCredentials: true,
        skipAuthRedirect: true,
      });
      
      setProgress(response.data);
      
      if (response.data.is_running) {
        setIsRunning(true);
        setTimeout(pollProgress, 1000);
      } else {
        setIsRunning(false);
      }
    } catch (error) {
      console.error('Error polling auto-match progress:', error);
      setIsRunning(false);
    }
  }, []);

  const startAutoMatch = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/copyright/auto-match/start`, {}, {
        withCredentials: true
      });
      
      if (response.data.success) {
        setIsRunning(true);
        setIsHidden(false);
        setIsMinimized(false);
        setProgress(response.data.progress);
        pollProgress();
        return { success: true, message: 'Otomatik eşleştirme başlatıldı' };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Error starting auto-match:', error);
      return { success: false, message: 'Otomatik eşleştirme başlatılamadı' };
    }
  };

  const rematchAll = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/copyright/bandrol/rematch-all`, {}, {
        withCredentials: true
      });

      if (response.data.success) {
        if (response.data.progress?.is_running) {
          setIsRunning(true);
          setIsHidden(false);
          setIsMinimized(false);
          setProgress(response.data.progress);
          pollProgress();
        } else {
          setProgress(response.data.progress || null);
          setIsRunning(false);
        }
        return {
          success: true,
          message: response.data.message,
          cleared: response.data.cleared,
        };
      }
      return { success: false, message: response.data.message };
    } catch (error) {
      console.error('Error rematching bandrol:', error);
      const detail = error.response?.data?.detail;
      return {
        success: false,
        message: detail || 'Yeniden eşleştirme başlatılamadı',
      };
    }
  };

  const stopAutoMatch = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/copyright/auto-match/stop`, {}, {
        withCredentials: true
      });
      setIsRunning(false);
      return { success: true, message: 'Otomatik eşleştirme durduruldu' };
    } catch (error) {
      console.error('Error stopping auto-match:', error);
      return { success: false, message: 'Durdurma hatası' };
    }
  };

  // Check status on mount
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/copyright/auto-match/status`, {
          withCredentials: true,
          skipAuthRedirect: true,
        });
        if (response.data.is_running) {
          setIsRunning(true);
          setProgress(response.data);
          pollProgress();
        }
      } catch (error) {
        // Ignore - user might not be logged in
      }
    };
    checkStatus();
  }, [pollProgress]);

  const value = {
    progress,
    isRunning,
    isMinimized,
    isHidden,
    setIsMinimized,
    setIsHidden,
    startAutoMatch,
    rematchAll,
    stopAutoMatch,
    pollProgress
  };

  return (
    <AutoMatchContext.Provider value={value}>
      {children}
    </AutoMatchContext.Provider>
  );
};

export default AutoMatchContext;
